// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicatorRounded,
} from '@/constants/hardfin-standard-icons';
import {
  CreateWorkflowWithStagesRequest,
  UpdateWorkflowRequest,
  UpdateWorkflowStageInRequest,
  Workflow,
  updateWorkflowRequestSchema,
} from '@/workflow/api/types';
import {
  createWorkflowWithStages,
  updateWorkflow,
} from '@/workflow/api/workflows';
import { WorkflowChangeConfirmationDialog } from '@/workflow/components/workflow-change-confirmation-dialog';
import { useWorkflows } from '@/workflow/hooks/use-workflows';
import {
  Edge,
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workflow?: Workflow; // Optional - if provided, edit mode; if not provided, create mode
}

interface WorkflowStageFormValue {
  id: string;
  name: string;
  orderIndex: number;
  inUseCount: number | null;
  tempId?: string;
  isNew?: boolean;
}

function toWorkflowStageFormValue(
  stage: Workflow['stages'][number]
): WorkflowStageFormValue {
  return {
    id: stage.id,
    name: stage.name,
    orderIndex: stage.orderIndex,
    inUseCount: stage.inUseCount,
  };
}

// Type-safe drag data interfaces
interface WorkflowStageDragData {
  type: 'workflow-stage';
  index: number;
}

// Type guard for drag data validation
const isWorkflowItemDragData = (
  data: unknown
): data is WorkflowStageDragData => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'workflow-stage' &&
    'index' in data &&
    typeof data.index === 'number'
  );
};

interface DraggableWorkflowItemProps {
  stage: WorkflowStageFormValue;
  index: number;
  onNameChange: (stageId: string, newName: string) => void;
  onDelete: (stageId: string) => void;
  isLoading: boolean;
  onDragDrop: (
    sourceIndex: number,
    targetIndex: number,
    edge: Edge | null
  ) => void;
  isLastPhase: boolean;
  error?: string;
}

// Draggable workflow stage component - following product fields pattern
const DraggableWorkflowItem = memo(function DraggableWorkflowItem(
  props: DraggableWorkflowItemProps
) {
  const { index, isLoading, onDragDrop } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  useEffect(() => {
    if (!containerRef.current || !handleRef.current) return;

    const cleanupDraggable = draggable({
      element: handleRef.current,
      canDrag: () => !isLoading,
      onDrop: () => setIsDragging(false),
      onDragStart: () => setIsDragging(true),
      getInitialData: () => ({
        type: 'workflow-stage',
        index: index,
      }),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: containerRef.current,
      getIsSticky: () => true,
      getData: ({ input, element }) => {
        return attachClosestEdge(
          {
            type: 'workflow-stage',
            index: index,
          },
          {
            input,
            element,
            allowedEdges: ['top', 'bottom'],
          }
        );
      },
      onDragEnter: ({ source, self }) => {
        if (!isWorkflowItemDragData(source.data) || source.data.index === index)
          return;
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDrag: ({ source, self }) => {
        if (!isWorkflowItemDragData(source.data) || source.data.index === index)
          return;
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => setClosestEdge(null),
      onDrop: ({ source, self }) => {
        setClosestEdge(null);
        if (!isWorkflowItemDragData(source.data) || source.data.index === index)
          return;
        onDragDrop(source.data.index, index, extractClosestEdge(self.data));
      },
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [index, isLoading, onDragDrop]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        p: 1.5,
        position: 'relative',
        opacity: isDragging ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
        backgroundColor: 'background.paper',
        // Top edge indicator
        '&::before':
          closestEdge === 'top'
            ? {
                content: '""',
                position: 'absolute',
                top: '-5px',
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: 'primary.main',
                borderRadius: 1,
              }
            : {},
        // Bottom edge indicator
        '&::after':
          closestEdge === 'bottom'
            ? {
                content: '""',
                position: 'absolute',
                bottom: '-5px',
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: 'primary.main',
                borderRadius: 1,
              }
            : {},
      }}
    >
      <IconButton ref={handleRef} size="small" tabIndex={-1}>
        <DragIndicatorRounded fontSize="small" />
      </IconButton>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 'fit-content' }}
      >
        Stage {props.stage.orderIndex}
      </Typography>
      <TextField
        label="Stage name"
        value={props.stage.name}
        onChange={(e) => props.onNameChange(props.stage.id, e.target.value)}
        size="small"
        fullWidth
        autoComplete="off"
        error={!!props.error}
        helperText={props.error}
        inputProps={{
          autoComplete: 'off',
        }}
      />
      <Tooltip
        arrow
        title={
          props.isLastPhase
            ? 'A workflow must have at least one stage'
            : 'Delete stage'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => props.onDelete(props.stage.id)}
            disabled={props.isLoading || props.isLastPhase}
            tabIndex={-1}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
});

export function WorkflowEditDialog(props: Props) {
  const queryClient = useQueryClient();
  const { data: workflows = [], refetch: refetchWorkflows } = useWorkflows({
    withCount: true,
  });
  const [stages, setStages] = useState<WorkflowStageFormValue[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [deletedStageIds, setDeletedStageIds] = useState<Set<string>>(
    new Set()
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<UpdateWorkflowRequest | null>(null);
  const [stageNameErrors, setStageNameErrors] = useState<
    Record<string, string>
  >({});

  const isCreateMode = !props.workflow;

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<UpdateWorkflowRequest>({
    resolver: zodResolver(updateWorkflowRequestSchema),
    defaultValues: {
      name: props.workflow?.name || '',
      isDefault: props.workflow?.isDefault || false,
    },
  });

  // Track if stages have been reordered or modified
  const [stagesModified, setStagesModified] = useState(false);

  const handleClose = () => {
    props.onClose();
    reset();
    setStages([]);
    setDeletedStageIds(new Set());
    setStageNameErrors({});
    setApiError(null);
    setStagesModified(false);
    setShowConfirmation(false);
    setPendingFormData(null);
  };

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateWorkflowRequest }) =>
      updateWorkflow(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      // Close dialog on success
      handleClose();
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const message = getUserFacingErrorMessage(apiError);
      if (message === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setApiError(null);
      } else {
        setApiError(message);
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateWorkflowWithStagesRequest) =>
      createWorkflowWithStages(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      // Close dialog on success
      handleClose();
    },
    onError: (error: AxiosError) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      const message = getUserFacingErrorMessage(apiError);
      if (message === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setApiError(null);
      } else {
        setApiError(message);
      }
    },
  });

  useEffect(() => {
    if (props.isOpen) {
      if (isCreateMode) {
        // Initialize for create mode with empty workflow and a set of default stages
        reset({
          name: '',
          isDefault: false,
        });
        const NUM_DEFAULT_STAGES = 3;
        const now = Date.now();
        const defaultStages: WorkflowStageFormValue[] = [];
        for (let i = 1; i <= NUM_DEFAULT_STAGES; i++) {
          const tempId = `temp-${now}-${i}`;
          defaultStages.push({
            id: tempId,
            name: '',
            orderIndex: i,
            tempId,
            isNew: true,
            inUseCount: 0,
          });
        }
        setStages(defaultStages);
      } else if (props.workflow) {
        // Initialize for edit mode with existing workflow
        reset({
          name: props.workflow.name,
          isDefault: props.workflow.isDefault,
        });
        setStages(props.workflow.stages.map(toWorkflowStageFormValue));
      }
      setDeletedStageIds(new Set());
      setStageNameErrors({});
      setStagesModified(false);
    }
  }, [props.isOpen, props.workflow, isCreateMode, reset]);

  // Stable ref for clearErrors
  const clearErrorsRef = useRef(clearErrors);
  clearErrorsRef.current = clearErrors;

  // Validate stage names (required and unique, excluding deleted stages)
  const validateStageNames = useCallback(() => {
    const activeStages = stages.filter(
      (stage) => !deletedStageIds.has(stage.id)
    );

    const newStageNameErrors: Record<string, string> = {};
    let hasErrors = false;

    // Check for required field errors
    activeStages.forEach((stage) => {
      const trimmedName = stage.name.trim();
      if (!trimmedName) {
        newStageNameErrors[stage.id] = 'Stage name is required';
        hasErrors = true;
      }
    });

    // Find duplicate names (only among stages with valid names)
    const nameGroups = new Map<string, string[]>();
    activeStages.forEach((stage) => {
      const normalizedName = stage.name.trim().toLowerCase();
      if (normalizedName) {
        if (!nameGroups.has(normalizedName)) {
          nameGroups.set(normalizedName, []);
        }
        nameGroups.get(normalizedName)!.push(stage.id);
      }
    });

    const duplicateStageIds = new Set<string>();

    nameGroups.forEach((stageIds) => {
      if (stageIds.length > 1) {
        stageIds.forEach((stageId) => duplicateStageIds.add(stageId));
      }
    });

    // Set duplicate errors (don't override required field errors)
    activeStages.forEach((stage) => {
      if (duplicateStageIds.has(stage.id) && !newStageNameErrors[stage.id]) {
        newStageNameErrors[stage.id] = 'Stage name must be unique';
        hasErrors = true;
      }
    });

    setStageNameErrors(newStageNameErrors);

    // Set root error message
    return !hasErrors;
  }, [stages, deletedStageIds]);

  const handleItemNameChange = useCallback(
    (stageId: string, newName: string) => {
      setStages((prevItems) =>
        prevItems.map((stage) =>
          stage.id === stageId ? { ...stage, name: newName } : stage
        )
      );
      setStagesModified(true);
    },
    []
  );

  const handleAddItem = () => {
    const tempId = `temp-${Date.now()}`;
    const newItem: WorkflowStageFormValue = {
      id: tempId,
      name: '',
      orderIndex: stages.length + 1,
      tempId,
      isNew: true,
      inUseCount: 0,
    };
    setStages([...stages, newItem]);
    setStagesModified(true);
  };

  const performDeleteItem = useCallback((stageId: string) => {
    // Track deleted stage for validation and API purposes
    setDeletedStageIds((prev) => new Set(prev).add(stageId));

    setStages((prevItems) => {
      const filtered = prevItems.filter((stage) => stage.id !== stageId);
      // Re-index remaining items
      return filtered.map((item, index) => ({
        ...item,
        orderIndex: index + 1,
      }));
    });
    setStagesModified(true);
  }, []);

  const handleDeleteItem = useCallback(
    (stageId: string) => {
      // Prevent deleting if only one stage remains
      if (stages.length <= 1) {
        return;
      }

      const stageToDelete = stages.find((stage) => stage.id === stageId);
      if (!stageToDelete) return;

      performDeleteItem(stageId);
    },
    [stages, performDeleteItem]
  );

  const handleDragDrop = useCallback(
    (sourceIndex: number, targetIndex: number, edge: Edge | null) => {
      if (sourceIndex === targetIndex) return;

      setStages((prevItems) => {
        // Build new array without using splice - dead simple approach
        const result = [];
        const draggedItem = prevItems[sourceIndex];
        let draggedItemAdded = false;

        // Add all items except the dragged item, inserting dragged item at target
        for (let i = 0; i < prevItems.length; i++) {
          if (i === targetIndex && edge === 'top' && !draggedItemAdded) {
            result.push(draggedItem);
            draggedItemAdded = true;
          }

          if (i !== sourceIndex) {
            result.push(prevItems[i]);
          }

          if (i === targetIndex && edge === 'bottom' && !draggedItemAdded) {
            result.push(draggedItem);
            draggedItemAdded = true;
          }
        }

        // Ensure dragged item is added if it wasn't already
        if (!draggedItemAdded) {
          result.push(draggedItem);
        }

        // Re-index all items
        return result.map((item, index) => ({
          ...item,
          orderIndex: index + 1,
        }));
      });

      setStagesModified(true);
    },
    []
  );

  const handleSaveAdd = async (formData: UpdateWorkflowRequest) => {
    // Validate stage names before saving
    if (!validateStageNames()) {
      return;
    }

    if (isCreateMode) {
      // For create mode, convert to CreateWorkflowWithStagesRequest
      const createData: CreateWorkflowWithStagesRequest = {
        name: formData.name || '',
        isDefault: formData.isDefault || false,
        stages: stages.map((stage) => ({
          name: stage.name,
          orderIndex: stage.orderIndex,
        })),
      };
      createMutation.mutate(createData);
      return;
    } else if (stagesModified && props.workflow) {
      // For edit mode, check if we need to show confirmation dialog
      // First refetch to get the latest workflow data with current project counts
      await refetchWorkflows();

      // Use original workflow structure but update with current project counts
      const latestWorkflow = getLatestWorkflow(props.workflow);
      const originalWithCurrentCounts = props.workflow.stages.map(
        (originalStage) => {
          const currentStage = latestWorkflow.stages.find(
            (s) => s.id === originalStage.id
          );
          return toWorkflowStageFormValue({
            ...originalStage,
            inUseCount: currentStage?.inUseCount || originalStage.inUseCount,
          });
        }
      );

      const hasSignificantChanges = checkForSignificantChanges(
        originalWithCurrentCounts,
        stages
      );

      if (hasSignificantChanges) {
        setPendingFormData(formData);
        setShowConfirmation(true);
        return;
      }

      performSave(props.workflow, formData);
    }
  };

  // Get the latest workflow data from the query
  const getLatestWorkflow = useCallback(
    (workflow: Workflow): Workflow => {
      return workflows.find((w) => w.key === workflow.key) || workflow;
    },
    [workflows]
  );

  const checkForSignificantChanges = (
    original: WorkflowStageFormValue[],
    modified: WorkflowStageFormValue[]
  ): boolean => {
    // Find deletions that affect projects
    // Only existing stages (with real IDs) can be considered for deletion detection
    const modifiedIds = new Set(
      modified.filter((s) => !s.isNew).map((s) => s.id)
    );
    const deletedStages = original.filter((s) => !modifiedIds.has(s.id));
    const hasProjectAffectingDeletions = deletedStages.some(
      (stage) => (stage.inUseCount || 0) > 0
    );

    // Check for position changes that affect projects
    // Only compare existing stages (ignore new stages completely)
    const existingStagesInModified = modified.filter((s) => !s.isNew);
    const modifiedMap = new Map(
      existingStagesInModified.map((stage) => [stage.id, stage])
    );
    const commonStages = original.filter((stage) => modifiedMap.has(stage.id));

    // Check if any existing stages changed relative position to other existing stages
    const hasProjectAffectingOrderChanges = commonStages.some((stageA, i) => {
      return commonStages.slice(i + 1).some((stageB) => {
        const modifiedStageA = modifiedMap.get(stageA.id)!;
        const modifiedStageB = modifiedMap.get(stageB.id)!;

        // Check if their relative order changed
        const originalABeforeB = stageA.orderIndex < stageB.orderIndex;
        const modifiedABeforeB =
          modifiedStageA.orderIndex < modifiedStageB.orderIndex;

        // Only consider it significant if it affects projects
        if (originalABeforeB !== modifiedABeforeB) {
          return (stageA.inUseCount || 0) > 0 || (stageB.inUseCount || 0) > 0;
        }
        return false;
      });
    });

    return hasProjectAffectingDeletions || hasProjectAffectingOrderChanges;
  };

  const performSave = (
    workflow: Workflow,
    formData: UpdateWorkflowRequest,
    migrations?: Record<string, string>
  ) => {
    // Prepare update data with items if modified
    const updateData: UpdateWorkflowRequest = {
      ...formData,
    };

    // Include items if they were modified
    if (stagesModified) {
      const allStages: UpdateWorkflowStageInRequest[] = [];

      // Add existing stages (both kept and modified)
      stages.forEach((item) => {
        allStages.push({
          id: item.isNew ? undefined : item.id,
          name: item.name,
          orderIndex: item.orderIndex,
          shouldDelete: false,
          migrateTo: null,
        });
      });

      // Add deleted stages with migration info (only for existing stages with real IDs)
      deletedStageIds.forEach((deletedStageId) => {
        const originalStage = workflow.stages.find(
          (s) => s.id === deletedStageId
        );
        // Only include if it's an existing stage (has a real ID and exists in original workflow)
        if (originalStage) {
          const migrationTargetName = migrations?.[deletedStageId] || null;

          allStages.push({
            id: originalStage.id,
            name: originalStage.name,
            orderIndex: originalStage.orderIndex,
            shouldDelete: true,
            migrateTo: migrationTargetName,
          });
        }
        // New items that were deleted are simply not included - no API notification needed
      });

      updateData.stages = allStages;
    }

    // Send single update request with all changes - let useMutation handle errors
    updateMutation.mutate({
      key: workflow.key,
      data: updateData,
    });
  };

  const handleConfirmChanges = (migrations?: Record<string, string>) => {
    if (props.workflow && pendingFormData) {
      setShowConfirmation(false);
      performSave(props.workflow, pendingFormData, migrations);
      setPendingFormData(null);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingFormData(null);
  };

  const isDirty = isFormDirty || stagesModified;
  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Dialog open={props.isOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isCreateMode ? 'Add workflow' : 'Edit workflow'}
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit(handleSaveAdd)}>
          <DialogContent>
            <Stack spacing={3}>
              <TextField
                label="Workflow name"
                size="small"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
                autoFocus
                autoComplete="off"
                inputProps={{
                  autoComplete: 'off',
                }}
              />
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">Stages</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    tabIndex={-1}
                  >
                    Add stage
                  </Button>
                </Box>
                {/*Todo - add divider*/}
                <Box sx={{ mt: 2, minHeight: '14rem' }}>
                  {/* Approximately 3 rows of stages */}
                  {stages.map((item, index) => (
                    <DraggableWorkflowItem
                      key={item.id}
                      stage={item}
                      index={index}
                      onNameChange={handleItemNameChange}
                      onDelete={handleDeleteItem}
                      isLoading={isLoading}
                      onDragDrop={handleDragDrop}
                      isLastPhase={stages.length === 1}
                      error={stageNameErrors[item.id]}
                    />
                  ))}
                </Box>
                {apiError && (
                  <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                    {apiError}
                  </Typography>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              disabled={!isDirty}
            >
              {isCreateMode ? 'Add workflow' : 'Save changes'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <SupportSnackbar
        isOpen={
          (createMutation.isError || updateMutation.isError) &&
          apiError === null &&
          !isPlanLimitError(createMutation.error)
        }
        message={
          isCreateMode
            ? 'Failed to create workflow'
            : 'Failed to update workflow'
        }
        onClose={() => {
          if (isCreateMode) {
            createMutation.reset();
          } else {
            updateMutation.reset();
          }
        }}
      />

      {props.workflow && (
        <WorkflowChangeConfirmationDialog
          isOpen={showConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmChanges}
          originalStages={getLatestWorkflow(props.workflow).stages.map(
            toWorkflowStageFormValue
          )}
          modifiedStages={stages}
          isLoading={isLoading}
        />
      )}
    </>
  );
}
